import {
  Box,
  Button,
  Card,
  Input,
  Pagination,
  Select,
  Table,
  Tag,
  Text,
} from '@nimbus-ds/components';
import { useEffect, useState, type ReactElement } from 'react';
import { api, type EstadoItem, type ItemCorrida, type PaginaItems } from '../api';
import { diferencia, motivo, pesos, porcentaje } from '../formato';
import { aErrorVisible, type ErrorVisible } from '../errores';
import { Cargando, ErrorCaja } from './Estado';
import { EnPantallaAncha, EnPantallaAngosta } from './SegunAncho';

interface Props {
  readonly corridaId: string;
  /** Se recarga cuando cambia, para reflejar el progreso de una corrida en curso. */
  readonly version?: number;
  /**
   * Desde dónde se muestra. Cambia sólo el texto de la tabla vacía, y cambia mucho:
   * "ninguno de tus productos coincide con esta regla" es accionable antes de confirmar,
   * y no significa nada mirando una corrida que ya terminó.
   */
  readonly contexto?: 'preview' | 'corrida';
}

const FILTROS: ReadonlyArray<{ valor: '' | EstadoItem; texto: string }> = [
  { valor: '', texto: 'Todas' },
  { valor: 'pending', texto: 'Se van a actualizar' },
  { valor: 'succeeded', texto: 'Actualizadas' },
  { valor: 'failed', texto: 'Fallidas' },
  { valor: 'skipped', texto: 'Sin cambios' },
];

/**
 * Tabla del preview y del detalle de corrida.
 *
 * Paginada y con buscador del lado del servidor: una tienda grande puede tener decenas
 * de miles de variantes y traerlas todas al navegador lo cuelga.
 *
 * *** Es la pantalla donde el comerciante decide, así que es la que más importa en un
 * celular. *** Seis columnas de números no entran en 375px. Abajo de 672px cada variante
 * pasa a ser una tarjeta, y en la tarjeta el orden de prioridad es: precio actual, precio
 * nuevo y la diferencia. Lo que se cae por falta de lugar es el SKU —que igual se puede
 * buscar por el buscador de arriba—, nunca la diferencia.
 */
export function TablaItems({
  corridaId,
  version = 0,
  contexto = 'corrida',
}: Props): ReactElement {
  const [pagina, setPagina] = useState(1);
  const [busqueda, setBusqueda] = useState('');
  const [filtro, setFiltro] = useState<'' | EstadoItem>('');
  const [datos, setDatos] = useState<PaginaItems | null>(null);
  const [error, setError] = useState<ErrorVisible | null>(null);

  // Rebote del buscador: no le pegamos al backend en cada tecla.
  const [busquedaAplicada, setBusquedaAplicada] = useState('');
  useEffect(() => {
    const temporizador = setTimeout(() => {
      setBusquedaAplicada(busqueda);
      setPagina(1);
    }, 300);
    return () => {
      clearTimeout(temporizador);
    };
  }, [busqueda]);

  useEffect(() => {
    let vigente = true;
    setError(null);
    api
      .items(corridaId, {
        pagina,
        q: busquedaAplicada,
        ...(filtro === '' ? {} : { estado: filtro }),
      })
      .then((respuesta) => {
        if (vigente) setDatos(respuesta);
      })
      .catch((excepcion: unknown) => {
        if (vigente) {
          setError(aErrorVisible(excepcion, 'No pudimos cargar el detalle. Probá de nuevo en un rato.'));
        }
      });
    return () => {
      vigente = false;
    };
  }, [corridaId, pagina, busquedaAplicada, filtro, version]);

  const totalPaginas = datos === null ? 1 : Math.max(1, Math.ceil(datos.total / datos.porPagina));

  return (
    <Card padding="none">
      <Card.Body>
        <Box
          display="flex"
          gap="2"
          padding={{ xs: '2', md: '4' }}
          flexWrap="wrap"
          alignItems="center"
        >
          <Box flex="1 1 260px">
            <Input.Search
              placeholder="Buscar por producto, variante o SKU"
              value={busqueda}
              onChange={(evento) => setBusqueda(evento.currentTarget.value)}
            />
          </Box>
          <Select
            id="filtro-estado"
            name="filtro-estado"
            value={filtro}
            onChange={(evento) => {
              setFiltro(evento.currentTarget.value as '' | EstadoItem);
              setPagina(1);
            }}
          >
            {FILTROS.map((opcion) => (
              <Select.Option key={opcion.valor} value={opcion.valor} label={opcion.texto} />
            ))}
          </Select>
          {datos !== null && (
            <Text fontSize="caption" color="neutral-textLow">
              {datos.total.toLocaleString('es-AR')} variantes
            </Text>
          )}
        </Box>

        {error !== null && (
          <Box padding="4">
            <ErrorCaja mensaje={error.mensaje} codigo={error.codigo} />
          </Box>
        )}

        {datos === null && error === null && <Cargando />}

        {datos !== null && datos.items.length === 0 && (
          <SinVariantes
            hayFiltro={busquedaAplicada.trim() !== '' || filtro !== ''}
            busqueda={busquedaAplicada.trim()}
            filtro={filtro}
            contexto={contexto}
            onLimpiar={() => {
              setBusqueda('');
              setFiltro('');
              setPagina(1);
            }}
          />
        )}

        {datos !== null && datos.items.length > 0 && (
          <>
            <EnPantallaAncha>
              <Table>
                <Table.Head>
                  <Table.Row>
                    <Table.Cell as="th">Producto</Table.Cell>
                    <Table.Cell as="th">Variante</Table.Cell>
                    <Table.Cell as="th">Precio actual</Table.Cell>
                    <Table.Cell as="th">Precio nuevo</Table.Cell>
                    <Table.Cell as="th">Diferencia</Table.Cell>
                    {hayColumnaEstado(datos.items) && <Table.Cell as="th">Estado</Table.Cell>}
                  </Table.Row>
                </Table.Head>
                <Table.Body>
                  {datos.items.map((item) => (
                    <Fila
                      key={item.variante}
                      item={item}
                      conEstado={hayColumnaEstado(datos.items)}
                    />
                  ))}
                </Table.Body>
              </Table>
            </EnPantallaAncha>

            <EnPantallaAngosta>
              {/* La Card de afuera va sin padding para que la tabla llegue al borde. */}
              <Box display="flex" flexDirection="column" gap="2" padding="2">
                {datos.items.map((item) => (
                  <TarjetaItem key={item.variante} item={item} />
                ))}
              </Box>
            </EnPantallaAngosta>
          </>
        )}

        {totalPaginas > 1 && (
          <Box display="flex" justifyContent="center" padding="4">
            <Pagination
              activePage={pagina}
              pageCount={totalPaginas}
              onPageChange={(nueva) => setPagina(nueva)}
            />
          </Box>
        )}
      </Card.Body>
    </Card>
  );
}

/**
 * La tabla vacía, que hasta ahora decía siempre lo mismo para tres cosas distintas.
 *
 * *** "No hay variantes que coincidan con la búsqueda" mentía dos de cada tres veces. ***
 * Se mostraba también sin ninguna búsqueda escrita, y entonces el comerciante buscaba un
 * filtro que no había puesto. Los tres casos son:
 *
 * - **Hay un filtro puesto.** Se dice cuál, y se ofrece sacarlo. Un botón, no una
 *   instrucción de a dónde ir a destildar algo.
 * - **Es un preview y la regla no agarró nada.** Es lo único accionable de los tres, y lo
 *   que hay que hacer es volver al paso 1: no hay nada que buscar acá.
 * - **Es una corrida que no tocó nada.** No hay nada que hacer; sólo hay que entender por
 *   qué, y el motivo no está en esta tabla sino arriba, en el resumen.
 */
function SinVariantes({
  hayFiltro,
  busqueda,
  filtro,
  contexto,
  onLimpiar,
}: {
  readonly hayFiltro: boolean;
  readonly busqueda: string;
  readonly filtro: '' | EstadoItem;
  readonly contexto: 'preview' | 'corrida';
  readonly onLimpiar: () => void;
}): ReactElement {
  if (hayFiltro) {
    const nombreDelFiltro = FILTROS.find((opcion) => opcion.valor === filtro)?.texto ?? '';
    return (
      <Box display="flex" flexDirection="column" gap="3" padding="6" alignItems="flex-start">
        <Text>
          {busqueda === ''
            ? `Ninguna variante está en "${nombreDelFiltro}".`
            : `Ninguna variante coincide con "${busqueda}"${
                filtro === '' ? '' : ` dentro de "${nombreDelFiltro}"`
              }.`}
        </Text>
        <Button onClick={onLimpiar}>Ver todas</Button>
      </Box>
    );
  }

  if (contexto === 'preview') {
    return (
      <Box display="flex" flexDirection="column" gap="1" padding="6">
        <Text>Ninguno de tus productos coincide con esta regla.</Text>
        <Text fontSize="caption" color="neutral-textLow">
          Volvé al paso 1 y probá ampliando el alcance: sacá un filtro de precio, sumá otra
          categoría, o elegí todo el catálogo.
        </Text>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap="1" padding="6">
      <Text>Esta actualización no llegó a tocar ninguna variante.</Text>
      <Text fontSize="caption" color="neutral-textLow">
        Puede ser que la regla no haya coincidido con ningún producto, o que la hayas frenado
        antes de que empezara. El resumen de arriba lo dice.
      </Text>
    </Box>
  );
}

/**
 * Si esta página tiene algo que poner en la columna "Estado".
 *
 * *** En una vista previa limpia la columna está vacía en todas las filas. *** Un preview
 * donde todo coincide son puras variantes `pending` sin avisos, y `SituacionDelItem` no
 * dibuja nada para eso: quedaba una columna con encabezado robándole ancho a los tres
 * números por los que el comerciante entró.
 *
 * Pero no se puede sacar siempre, porque hay tres casos en que sí dice algo y son los que
 * más importan antes de confirmar:
 *
 *  - una variante `skipped`, con el motivo por el que se cae del plan;
 *  - una `pending` con avisos, que es la etiqueta "Revisar" y el aviso en rojo;
 *  - en una corrida ya terminada, "Actualizada" o "Falló" con el error.
 *
 * Así que se decide por página: si ninguna fila tiene nada, la columna no existe. Se mira
 * la página y no la corrida entera porque es lo que está a la vista y es lo único que se
 * puede saber sin otra consulta — el que filtra por "Sin cambios" la ve aparecer.
 */
function hayColumnaEstado(items: readonly ItemCorrida[]): boolean {
  return items.some(
    (item) =>
      item.estado !== 'pending' ||
      item.avisos.length > 0 ||
      item.motivo !== null ||
      item.error !== null,
  );
}

function Fila({
  item,
  conEstado,
}: {
  readonly item: ItemCorrida;
  readonly conEstado: boolean;
}): ReactElement {
  return (
    <Table.Row>
      <Table.Cell>
        <Box display="flex" flexDirection="column" gap="1">
          <Text>{item.producto}</Text>
          {item.sku !== null && (
            <Text fontSize="caption" color="neutral-textLow">
              {item.sku}
            </Text>
          )}
        </Box>
      </Table.Cell>
      <Table.Cell>{item.etiqueta}</Table.Cell>
      <Table.Cell>{pesos(item.precioActual)}</Table.Cell>
      <Table.Cell>
        <Box display="flex" flexDirection="column" gap="1">
          <Text>{pesos(item.precioNuevo)}</Text>
          <Oferta item={item} />
        </Box>
      </Table.Cell>
      <Table.Cell>
        <Box display="flex" flexDirection="column" gap="1">
          <Text>{diferencia(item.precioActual, item.precioNuevo)}</Text>
          <Text fontSize="caption" color="neutral-textLow">
            {porcentaje(item.precioActual, item.precioNuevo)}
          </Text>
        </Box>
      </Table.Cell>
      {conEstado && (
        <Table.Cell>
          <SituacionDelItem item={item} />
        </Table.Cell>
      )}
    </Table.Row>
  );
}

/**
 * La misma variante, en 375px.
 *
 * El renglón de precios es lo primero que se lee: el actual apagado, la flecha, el nuevo
 * en negrita. Abajo la diferencia, sola en su renglón, que es el número por el que el
 * comerciante aprieta o no aprieta el botón. El SKU no está: es lo único que se puede
 * sacar sin que la tarjeta pierda sentido, y el buscador de arriba sigue encontrándolo.
 */
function TarjetaItem({ item }: { readonly item: ItemCorrida }): ReactElement {
  return (
    <Box
      display="flex"
      flexDirection="column"
      gap="2"
      padding="3"
      borderStyle="solid"
      borderWidth="1"
      borderColor="neutral-surface"
      borderRadius="2"
    >
      <Box display="flex" flexDirection="column">
        <Text>{item.producto}</Text>
        <Text fontSize="caption" color="neutral-textLow">
          {item.etiqueta}
        </Text>
      </Box>

      <Box display="flex" gap="2" alignItems="baseline" flexWrap="wrap">
        <Text color="neutral-textLow">{pesos(item.precioActual)}</Text>
        <Text color="neutral-textLow">→</Text>
        <Text fontWeight="bold">{pesos(item.precioNuevo)}</Text>
      </Box>

      <Text fontWeight="medium">
        {diferencia(item.precioActual, item.precioNuevo)} ·{' '}
        {porcentaje(item.precioActual, item.precioNuevo)}
      </Text>

      <Oferta item={item} />

      <SituacionDelItem item={item} />
    </Box>
  );
}

/**
 * La oferta de la variante, siempre diciendo qué le pasó.
 *
 * *** Era el único número de la app que aparecía sin contexto. *** Antes decía
 * "Oferta: $13.287" a secas, debajo del precio nuevo, y no había forma de saber si ese
 * número era el de antes o el de después. En una app de precios, un número del que no se
 * sabe qué le pasó hace dudar de todos los demás de la pantalla.
 *
 * La regla de "cambió" es la misma que usan la ejecución y el deshacer
 * (`camposEscritos` en el backend): hay oferta nueva y es distinta de la actual. El motor
 * arrastra la oferta actual cuando la regla no la toca, así que "igual" significa
 * literalmente que no se va a escribir.
 */
function Oferta({ item }: { readonly item: ItemCorrida }): ReactElement | null {
  // Sin oferta ni antes ni después: no hay nada que decir. Inventar un renglón "sin
  // oferta" en cada variante sería ruido en la mayoría de las filas.
  if (item.ofertaNueva === null && item.ofertaActual === null) return null;

  if (item.ofertaNueva === null || item.ofertaNueva === item.ofertaActual) {
    return (
      <Text fontSize="caption" color="neutral-textLow">
        Oferta: {pesos(item.ofertaActual)} (sin cambios)
      </Text>
    );
  }

  return (
    <Text fontSize="caption" color="neutral-textLow">
      Oferta: {pesos(item.ofertaActual)} → {pesos(item.ofertaNueva)}
    </Text>
  );
}

/**
 * Estado, motivo, avisos y error de una variante.
 *
 * Compartido entre la tabla y la tarjeta a propósito: es la parte que decide si el
 * comerciante entiende por qué una variante no se tocó, y dos copias se habrían separado
 * en el primer código de motivo nuevo.
 */
function SituacionDelItem({ item }: { readonly item: ItemCorrida }): ReactElement {
  const hayAvisos = item.avisos.length > 0;

  return (
    <Box display="flex" flexDirection="column" gap="1" alignItems="flex-start">
      {item.estado === 'skipped' && <Tag appearance="neutral">Sin cambios</Tag>}
      {item.estado === 'succeeded' && <Tag appearance="success">Actualizada</Tag>}
      {item.estado === 'failed' && <Tag appearance="danger">Falló</Tag>}
      {item.estado === 'pending' && hayAvisos && <Tag appearance="warning">Revisar</Tag>}

      {item.motivo !== null && (
        <Text fontSize="caption" color="neutral-textLow">
          {motivo(item.motivo)}
        </Text>
      )}
      {/* Los avisos van en rojo: son los que el comerciante tiene que mirar. */}
      {item.avisos.map((aviso) => (
        <Text key={aviso} fontSize="caption" color="danger-textLow">
          {aviso}
        </Text>
      ))}
      {item.error !== null && (
        <Text fontSize="caption" color="danger-textLow">
          {item.error}
        </Text>
      )}
    </Box>
  );
}
